// Home — the per-learner landing (MY_GUIDES gallery). Answers "play or make?"
// with a Continue hero + a persistent Create bar, and lists recent sets
// newest-first. Four states by set count. Pressed "soft-ink" theme; the app's
// LeftPanel is the sidebar, so this renders only the main content column.
// Design: specs/home-page-july6 (HomeC3).
//
// State + handlers live in useHomeView (src/hooks) — this file is the
// presentation layer only. Styling lives alongside it in home.css.

import type { CSSProperties } from 'react';
import { cn } from '@/lib/utils';
import { useHomeView } from '@/hooks/useHomeView';
import { AddLearnerEmptyState } from '@/components/domain/AddLearnerEmptyState';
import type { HomeSet } from './homeSets';
import { SetCover, BandChip, DateStamp, GameGlyph, Mark } from './HomePieces';
import './home.css';
import { MaskedName, PH_MASK } from '@/lib/privacyMask';import { CssVarSpan } from "@/components/CssVarElements";

export function HomeView() {
  const view = useHomeView();
  if (!view.activeLearner) return <AddLearnerEmptyState />;

  const { name, initial, ink, stageName, guides, isLoading, sets, empty, hero, rest, shownRest, expanded, setExpanded, isSoundExplorer, openSet, printSet, browseBooks, uploadPages, viewProfile } = view;

  return (
    <div className="home-root hs-brand">
      <div className="home-grain" />
      <main className="home-main home-stage">
        <IdentityBar name={name} initial={initial} ink={ink} stageName={stageName} mark={!empty} onViewProfile={viewProfile} />
        {isLoading && guides.length === 0 ?
        <div className="home-view__loading">Loading <MaskedName>{name}</MaskedName>&rsquo;s sets…</div> :
        isSoundExplorer ?
        <SoundExplorerHome onViewProfile={viewProfile} /> :
        empty ?
        <EmptyState name={name} onBrowse={browseBooks} onUpload={uploadPages} /> :

        <>
            <HeroSet set={hero} onPlay={() => openSet(hero)} onPrint={() => printSet(hero)} />
            <CreateBar name={name} onBrowse={browseBooks} onUpload={uploadPages} />
            {rest.length > 0 &&
          <div>
                <div className="home-view__recent-header">
                  <span className="home-view__recent-eyebrow">Recent sets</span>
                  <span className="home-view__recent-count">{sets.length} in all</span>
                </div>
                <div className="home-view__recent-list">
                  {shownRest.map((s) => <SetRow key={s.id} set={s} onOpen={() => openSet(s)} />)}
                </div>
                {rest.length > 4 &&
            <div className="home-view__view-all-wrap">
                    <button
                type="button" onClick={() => setExpanded((v) => !v)} className="hs-press hs-press--paper home-view__view-all-btn">

                      {expanded ? 'Show less' : `View all (${sets.length})`}
                      <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="var(--ink)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className={cn('home-view__view-all-chevron', expanded && 'home-view__view-all-chevron--expanded')}><path d="M5 8l5 5 5-5" /></svg>
                    </button>
                  </div>
            }
              </div>
          }
          </>
        }
      </main>
    </div>);

}

// ── Identity bar ─────────────────────────────────────────────────────────────
function IdentityBar({ name, initial, ink, stageName, mark, onViewProfile

}: {name: string;initial: string;ink: string;stageName: string | null;mark: boolean;onViewProfile: () => void;}) {
  return (
    <div className="home-view__identity-bar">
      <div className="home-view__identity-left">
        <CssVarSpan className="home-view__avatar" cssVars={{ '--avatar-ink': ink } as CSSProperties} {...PH_MASK}>{initial}</CssVarSpan>
        <div className="home-view__identity-name-wrap">
          <div className="home-view__identity-name" {...PH_MASK}>{mark ? <Mark h={11} bottom={1}>{name}</Mark> : name}</div>
          <div className={cn('home-view__identity-stage', stageName && 'home-view__identity-stage--set')}>{stageName ?? 'Placement not done yet'}</div>
        </div>
      </div>
      <button type="button" onClick={onViewProfile} className="home-view__view-profile-btn">
        View profile
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 10h11M11 5l5 5-5 5" /></svg>
      </button>
    </div>);

}

// ── Continue hero ────────────────────────────────────────────────────────────
function HeroSet({ set, onPlay, onPrint }: {set: HomeSet;onPlay: () => void;onPrint: () => void;}) {
  const isBuilding = set.generationStatus === 'GENERATING';

  return (
    <div className="home-view__hero">
      <div className="home-view__hero-cover">
        <SetCover id={set.id} title={set.title} author={set.author} band={set.band} fromYou={set.fromYou} w={164} standing />
      </div>
      <div className="home-view__hero-body">
        <div className="home-view__hero-top-row">
          <span className="home-view__hero-eyebrow">Newest set</span>
          <span className="home-view__hero-made">made {set.created}</span>
        </div>
        <div className="home-view__hero-title">{set.title}</div>
        <div className="home-view__hero-meta">
          {set.author && <span className="home-view__hero-author">{set.author}</span>}
          {set.author && set.band && <span className="home-view__hero-dot" />}
          {set.band && <BandChip band={set.band} />}
        </div>
        <div className="home-view__hero-games">
          {set.games.map((g) =>
          <span key={g} className="home-view__hero-game-chip">
              <GameGlyph name={g} size={16} />
              <span className="home-view__hero-game-label">{g}</span>
            </span>
          )}
        </div>
        <div className="home-view__hero-actions">
          <button type="button" onClick={onPlay} className="hs-press home-view__hero-play-btn">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="#fff"><path d="M6 4l11 6-11 6z" /></svg>
            {isBuilding ? 'View progress' : 'Ready to play'}
          </button>
          {!isBuilding &&
          <button type="button" onClick={onPrint} className="hs-press hs-press--paper home-view__hero-print-btn">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--ink)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9V3h12v6M6 18H4a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-2M8 14h8v7H8z" /></svg>
              Print for the table
            </button>
          }
        </div>
      </div>
    </div>);

}

// ── Create bar ───────────────────────────────────────────────────────────────
function CreateBar({ name, onBrowse, onUpload }: {name: string;onBrowse: () => void;onUpload: () => void;}) {
  return (
    <div className="home-view__create-bar">
      <div className="home-view__create-bar-left">
        <div className="home-view__create-bar-icon">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="var(--ink)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 4.5v11M4.5 10h11" /></svg>
        </div>
        <div className="home-view__create-bar-text">
          <div className="home-view__create-bar-title">Make another set for <MaskedName>{name}</MaskedName></div>
          <div className="home-view__create-bar-sub">Turn any book into four games, to play on screen or print.</div>
        </div>
      </div>
      <div className="home-view__create-bar-actions">
        <button type="button" onClick={onBrowse} className="hs-press home-view__create-bar-browse-btn">
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 5C8 3.5 5 3.5 3 4v12c2-.5 5-.5 7 1 2-1.5 5-1.5 7-1V4c-2-.5-5-.5-7 1zM10 5v13" /></svg>
          Browse books
          <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 10h11M11 5l5 5-5 5" /></svg>
        </button>
        <button type="button" onClick={onUpload} className="hs-press hs-press--paper home-view__create-bar-upload-btn">
          <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="var(--ink)" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round"><path d="M10 14V5M6 9l4-4 4 4M5 15h10" /></svg>
          Upload pages
        </button>
      </div>
    </div>);

}

// ── Recent set row (icons-only) ──────────────────────────────────────────────
function SetRow({ set, onOpen }: {set: HomeSet;onOpen: () => void;}) {
  return (
    <button
      type="button" onClick={onOpen} className="home-row home-view__set-row">

      <div className="home-view__set-row-cover">
        <SetCover id={set.id} title={set.title} author={set.author} band={set.band} fromYou={set.fromYou} w={54} />
      </div>
      <div className="home-view__set-row-info">
        <div className="home-view__set-row-title-row">
          <span className="home-view__set-row-title">{set.title}</span>
          {set.author && <span className="home-view__set-row-author">{set.author}</span>}
        </div>
        <div className="home-view__set-row-meta">
          {set.band && <BandChip band={set.band} />}
          {set.band && <span className="home-view__set-row-divider" />}
          {set.games.map((g) =>
          <span key={g} title={g} className="home-view__set-row-game-icon">
              <GameGlyph name={g} size={16} />
            </span>
          )}
        </div>
      </div>
      <span className="home-view__set-row-date">
        <DateStamp text={'Created ' + set.created} />
      </span>
      <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="var(--ink)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="home-view__set-row-chevron"><path d="M7 4l6 6-6 6" /></svg>
    </button>);

}

// ── Sound Explorer (stage 0) waitlist hero ───────────────────────────────────
// No book games exist for this stage yet, so instead of "browse/upload"
// CTAs (which don't apply) this sets an honest expectation with a
// "notify me" waitlist card. Design: sound-explorer-handoff-july24 (SeHome).
function SoundExplorerHome({ onViewProfile }: {onViewProfile: () => void;}) {
  return (
    <div className="home-view__sx">
      <div className="home-view__sx-shadow" />
      <div className="home-view__sx-card">
        <span className="home-view__sx-deco-1" />
        <span className="home-view__sx-deco-2" />
        <span className="home-view__sx-deco-3" />
        <div className="home-view__sx-badge-row">
          <span className="home-view__sx-badge-icon">
            <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="var(--ink)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="10" cy="10" r="7.5" />
              <path d="M10 6v4l2.5 2" />
            </svg>
          </span>
          <span className="hs-eyebrow home-view__sx-badge-label">On the waitlist</span>
        </div>
        <h2 className="home-view__sx-heading">
          Book Games for Sound Explorers are coming.
        </h2>
        <p className="home-view__sx-copy">
          We&rsquo;re building practice for the sounds, letters, and listening skills that come before reading.
        </p>
        <div className="home-view__sx-actions">
          <button type="button" className="hs-press home-view__sx-notify-btn">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3 6l7 5 7-5M3 6v9h14V6M3 6h14" />
            </svg>
            Notify me when they launch
          </button>
          <button type="button" onClick={onViewProfile} className="hs-press hs-press--paper home-view__sx-view-btn">
            View the full evaluation
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="var(--ink)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 10h11M11 5l5 5-5 5" /></svg>
          </button>
        </div>
      </div>
    </div>);

}

// ── Empty state ──────────────────────────────────────────────────────────────
function EmptyState({ name, onBrowse, onUpload }: {name: string;onBrowse: () => void;onUpload: () => void;}) {
  const browseBtn =
  <button type="button" onClick={onBrowse} className="hs-press home-view__empty-browse-btn">
      <svg width="22" height="22" viewBox="0 0 20 20" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 5C8 3.5 5 3.5 3 4v12c2-.5 5-.5 7 1 2-1.5 5-1.5 7-1V4c-2-.5-5-.5-7 1zM10 5v13" /></svg>
      <span>Browse books for <MaskedName>{name}</MaskedName></span>
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 10h11M11 5l5 5-5 5" /></svg>
    </button>;

  const uploadBtn =
  <button type="button" onClick={onUpload} className="hs-press hs-press--paper home-view__empty-upload-btn">
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="var(--ink)" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round"><path d="M10 14V5M6 9l4-4 4 4M5 15h10" /></svg>
      Upload pages
    </button>;

  return (
    <>
      <h2 className="home-view__empty-heading">
        Let&rsquo;s make <MaskedName>{name}</MaskedName>&rsquo;s first <Mark h={15} bottom={4}>games</Mark>.
      </h2>
      <p className="home-view__empty-copy">
        Pick a book <MaskedName>{name}</MaskedName> loves and Hopscotch turns it into a set of games that build the skills and confidence to read it.
      </p>
      <div className="home-view__empty-actions">{browseBtn}{uploadBtn}</div>
      <div className="home-view__empty-card">
        <div className="home-view__empty-card-icon">
          <span className="home-view__empty-card-spine" />
          <svg width="24" height="24" viewBox="0 0 20 20" fill="none" stroke="rgba(27,26,64,0.4)" strokeWidth="2" strokeLinecap="round"><path d="M10 4v12M4 10h12" /></svg>
        </div>
        <div>
          <div className="home-view__empty-card-title">No games yet</div>
          <div className="home-view__empty-card-copy">Everything you make for <MaskedName>{name}</MaskedName> lands here, newest first, ready to play.</div>
        </div>
      </div>
    </>);

}